import styled from 'styled-components'
import dayjs from 'dayjs'
import { Text } from '@lmc/ui-kit/src/general'
import { getMedicationIcon } from '../utils/medicationUtils'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { SectionHeader } from '../componentsV2/SectionHeader'
import {
  StockRecord,
  MedicationStockReason,
  AdministrationStatus
} from '../types'
import { getActionTitle } from '../utils/stockRecordUtils'
import { getAdministrationStatusDisplay } from '../utils/administrationUtils'
import { ImageGalleryItem } from '../componentsV2/StockRecordDrawer/ImageGalleryItem'
import { FileGalleryItem } from '../componentsV2/StockRecordDrawer/FileGalleryItem'

type RecordsSectionProps = {
  records?: StockRecord[]
  medicationName: string
  residentFirstName: string
  residentFullName: string
  onViewRecord: (recordId: string) => void
  onViewAdministrationRecord: (recordId: string) => void
}

const isAdministrationRecord = (record: StockRecord): boolean => {
  return record.type === 'administration'
}

const RecordsSection = ({
  records = [],
  medicationName,
  residentFirstName,
  residentFullName,
  onViewRecord,
  onViewAdministrationRecord
}: RecordsSectionProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const formatTimestamp = (date: string) => {
    return dayjs(date).format('ddd DD-MM-YYYY HH:mm')
  }

  const formatDate = (date: string) => {
    return dayjs(date).format('DD-MM-YYYY')
  }

  const formatTime = (date: string) => {
    return dayjs(date).format('HH:mm')
  }

  const getRecordedByName = (record: StockRecord): string => {
    const nameValue = record.recordedBy.name as any
    if (nameValue && typeof nameValue === 'object' && 'first' in nameValue) {
      return `${nameValue.first} ${nameValue.last}`
    }
    return typeof nameValue === 'string' ? nameValue : ''
  }

  const getWitnessName = (record: StockRecord): string | undefined => {
    if (!record.witnesses || record.witnesses.length === 0) return undefined
    const witness = record.witnesses[0] as any
    if (witness.name) {
      if (typeof witness.name === 'object' && 'first' in witness.name) {
        return `${witness.name.first} ${witness.name.last}`
      }
      return witness.name
    }
    return undefined
  }

  const getAdministrationTitle = (
    record: StockRecord,
    status: AdministrationStatus | string
  ): string => {
    if (status === AdministrationStatus.GIVEN) {
      return t('ADMINISTERED')
    }
    const statusDisplay = getAdministrationStatusDisplay(
      status as AdministrationStatus
    )
    return `${t('NOT_ADMINISTERED')}: ${t(statusDisplay.translationKey)}`
  }

  const getAdministrationDescription = (record: StockRecord): string => {
    const adminData = record.administration
    if (!adminData) return ''

    const status = (adminData.administrationStatus ||
      record.administrationStatus) as AdministrationStatus | string
    const isPrn = adminData.prn || false
    const doseQuantity = adminData.doseQuantity || 0
    const unit = record.unit || ''
    const residentName =
      adminData.resident?.firstName ||
      adminData.resident?.name?.first ||
      residentFirstName ||
      ''
    const residentFull = adminData.resident
      ? `${adminData.resident.name?.first || ''} ${adminData.resident.name?.last || ''}`.trim()
      : residentFullName

    if (status === AdministrationStatus.GIVEN) {
      if (isPrn) {
        return `${residentName} was administered ${doseQuantity} ${unit} ${medicationName}.`
      } else {
        const time = adminData.startTime ? formatTime(adminData.startTime) : ''
        return `For ${residentFull}'s ${time} scheduled meds, ${doseQuantity} ${unit} ${medicationName} was administered`
      }
    } else {
      const statusDisplay = getAdministrationStatusDisplay(
        status as AdministrationStatus
      )
      const reasonCode = adminData.reasonCode || statusDisplay.translationKey
      const reason = t(statusDisplay.translationKey)
      const time = adminData.startTime ? formatTime(adminData.startTime) : ''
      const stockUpdated =
        adminData.stock?.quantityChange !== undefined &&
        adminData.stock.quantityChange !== 0
      const stockLevelText = stockUpdated
        ? t('WAS_UPDATED')
        : t('WAS_NOT_UPDATED')
      return `For ${residentFull}'s ${time} scheduled meds, ${medicationName} was not administered. Reason code: ${reasonCode} - ${reason}. Stock level ${stockLevelText}`
    }
  }

  const getStockDescription = (record: StockRecord): string => {
    const action = record.action
    const quantity = Math.abs(record.quantityChange || 0)
    const unit = record.unit || ''

    switch (action) {
      case MedicationStockReason.INITIAL_STOCK:
        return `${medicationName} was added with an initial stock of ${quantity} ${unit}`
      case MedicationStockReason.STOCK_COUNT:
      case MedicationStockReason.RECONCILIATION:
        const expected = record.expectedStock || 0
        const counted = record.stockCount || 0
        return `${residentFirstName}'s medication was counted. For ${medicationName}, ${expected} ${unit} were expected, ${counted} ${unit} were counted.`
      case MedicationStockReason.DISPOSAL:
        return `For ${medicationName}, ${quantity} ${unit} were disposed.`
      case MedicationStockReason.RESTOCK:
        return `For ${medicationName}, ${quantity} ${unit} were added.`
      case MedicationStockReason.NOT_ADMINISTERED:
        const notesText = Array.isArray(record.notes)
          ? record.notes.join(', ')
          : record.notes || ''
        return notesText || ''
      default:
        return ''
    }
  }

  const getTitle = (record: StockRecord): string => {
    if (record.type === 'administration') {
      const status = (record.administration?.administrationStatus ||
        record.administrationStatus) as AdministrationStatus | string
      return getAdministrationTitle(record, status)
    }

    if (record.action === MedicationStockReason.NOT_ADMINISTERED) {
      return t('MEDICATION_COMMENT')
    }

    if (record.action === MedicationStockReason.INITIAL_STOCK) {
      return t('MEDICATION_ADDED')
    }

    return getActionTitle(record.action, t, record.administrationStatus)
  }

  const getDescription = (record: StockRecord): string => {
    if (record.type === 'administration') {
      return getAdministrationDescription(record)
    }

    if (record.action === MedicationStockReason.NOT_ADMINISTERED) {
      const notesText = Array.isArray(record.notes)
        ? record.notes.join(', ')
        : record.notes || ''
      return notesText || ''
    }

    return getStockDescription(record)
  }

  const getEditedBy = (record: StockRecord) => {
    if (!record.changelog || record.changelog.length === 0) return undefined
    const latestEntry = record.changelog[record.changelog.length - 1]
    const changedByName =
      typeof latestEntry.changedBy.name === 'object' &&
      latestEntry.changedBy.name !== null
        ? `${(latestEntry.changedBy.name as any).first} ${(latestEntry.changedBy.name as any).last}`
        : latestEntry.changedBy.name || ''
    return {
      name: changedByName,
      date: latestEntry.changedAt.toString()
    }
  }

  return (
    <RecordsContainer>
      <SectionHeader title={t('RECORDS')} />
      <RecordsList>
        {records.map((record) => {
          const isVideo = (url: string, contentType?: string) => {
            if (contentType?.startsWith('video/')) return true
            return /\.(mp4|webm|ogg|mov)/i.test(url)
          }

          const isImage = (url: string, contentType?: string) => {
            if (contentType?.startsWith('image/')) return true
            return /\.(jpg|jpeg|png|gif|webp)/i.test(url)
          }

          return (
            <RecordCard
              key={record.id}
              onClick={() => {
                if (isAdministrationRecord(record)) {
                  onViewAdministrationRecord(record.id)
                } else {
                  onViewRecord(record.id)
                }
              }}
            >
              <RecordContent>
                <RecordIconContainer>
                  {getMedicationIcon(undefined, 'small')}
                </RecordIconContainer>
                <RecordDetails>
                  <RecordTimestamp level={2}>
                    {formatTimestamp(record.actionTime || record.date)} ·{' '}
                    {getRecordedByName(record)}
                    {!(record.action === MedicationStockReason.INITIAL_STOCK) &&
                      (record.type === 'administration' ||
                        (record.witnesses && record.witnesses.length > 0)) &&
                      getWitnessName(record) &&
                      `, ${t('WITNESSED_BY')} ${getWitnessName(record)}`}
                  </RecordTimestamp>
                  <RecordAction>{getTitle(record)}</RecordAction>
                  <RecordDescription level={2}>
                    {getDescription(record)}
                  </RecordDescription>
                  {record.action === MedicationStockReason.NOT_ADMINISTERED &&
                    record.mediaAttachments &&
                    record.mediaAttachments.length > 0 && (
                      <AttachmentsContainer>
                        {record.mediaAttachments.map((attachment, index) => {
                          if (isVideo(attachment.url, attachment.contentType)) {
                            return (
                              <VideoThumbnail key={attachment._id || index}>
                                <VideoThumbnailVideo
                                  src={attachment.url}
                                  muted
                                  onLoadedData={(
                                    e: React.SyntheticEvent<HTMLVideoElement>
                                  ) => {
                                    const video = e.target as HTMLVideoElement
                                    video.currentTime = 0.1
                                  }}
                                />
                              </VideoThumbnail>
                            )
                          }

                          if (isImage(attachment.url, attachment.contentType)) {
                            return (
                              <ImageGalleryItem
                                key={attachment._id || index}
                                src={attachment.url}
                                alt={`Image attachment ${index + 1}`}
                              />
                            )
                          }

                          return (
                            <FileGalleryItem
                              key={attachment._id || index}
                              url={attachment.url}
                              fileName={attachment.key?.split('/').pop()}
                            />
                          )
                        })}
                      </AttachmentsContainer>
                    )}
                  {getEditedBy(record) && (
                    <RecordEditInfo level={2}>
                      {t('EDITED_BY')} {getEditedBy(record)?.name} {t('ON')}{' '}
                      {formatDate(getEditedBy(record)?.date || '')}
                    </RecordEditInfo>
                  )}
                </RecordDetails>
              </RecordContent>
            </RecordCard>
          )
        })}
      </RecordsList>
    </RecordsContainer>
  )
}

const RecordsContainer = styled.div`
  margin-top: 32px;
`

const RecordsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  padding-bottom: 24px;
`

const RecordCard = styled.div`
  background-color: ${({ theme }) => theme.color.neutral.color11};
  border-radius: 8px;
  padding: 12px 16px;
  max-width: 640px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.color.neutral.color10};
  }
`

const RecordContent = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`

const RecordIconContainer = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 100%;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
`

const RecordDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const RecordTimestamp = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  font-weight: 500;
`

const RecordAction = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 700;
`

const RecordDescription = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 500;
`

const RecordEditInfo = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  font-weight: 500;
`

const AttachmentsContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`

const VideoThumbnail = styled.div`
  position: relative;
  width: 58px;
  height: 72px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
`

const VideoThumbnailVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export default RecordsSection
